import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/axios';

const useActivityTracker = () => {
  const { isAuthenticated, handleSessionExpiration } = useAuth();
  const heartbeatIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  
  // Make debug functions globally accessible
  if (typeof window !== 'undefined') {
    window.getDebugLogs = () => {
      const logs = JSON.parse(localStorage.getItem('debugLogs') || '[]');
      console.log('=== DEBUG LOGS ===');
      logs.forEach(log => {
        console.log(`${log.timestamp}: ${log.message}`);
        if (log.error) console.log('Error details:', log.error);
      });
      console.log('=== END DEBUG LOGS ===');
      return logs;
    };
    
    window.clearDebugLogs = () => {
      localStorage.removeItem('debugLogs');
      console.log('Debug logs cleared');
    };
  }
  
  // Activity timeout: 1 hour (3600000 ms) - production setting
  const ACTIVITY_TIMEOUT = 3600000;
  
  // Warning threshold: log approaching inactivity at 55 minutes (3300000 ms)
  const WARNING_THRESHOLD = 3300000;
  
  // Heartbeat interval: every 5 minutes (300000 ms) - production setting
  const HEARTBEAT_INTERVAL = 300000;

  // JWT token refresh threshold: refresh when less than 5 minutes remaining
  const JWT_REFRESH_THRESHOLD = 300000; // 5 minutes

  // JWT token utility functions
  const getTokenExpiry = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return null;
      
      // Decode JWT token to get expiry
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000; // Convert to milliseconds
    } catch (error) {
      console.warn('Failed to decode JWT token:', error);
      return null;
    }
  }, []);

  const refreshJWTToken = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        console.warn('No refresh token available');
        return false;
      }

      console.log('Refreshing JWT token due to user activity...');
      const response = await api.post('/api/token/refresh/', {
        refresh: refreshToken
      });

      // Update tokens
      const newAccessToken = response.data.access;
      localStorage.setItem('accessToken', newAccessToken);
      
      // Update axios default header
      api.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
      
      console.log('JWT token refreshed successfully due to user activity');
      
      // Store success in localStorage for debugging
      const debugLogs = JSON.parse(localStorage.getItem('debugLogs') || '[]');
      debugLogs.push({
        timestamp: new Date().toISOString(),
        message: 'JWT token refreshed successfully due to user activity',
        type: 'jwt_refresh_success'
      });
      localStorage.setItem('debugLogs', JSON.stringify(debugLogs));
      
      return true;
    } catch (error) {
      console.warn('Failed to refresh JWT token:', error);
      
      // Store error in localStorage for debugging
      const debugLogs = JSON.parse(localStorage.getItem('debugLogs') || '[]');
      debugLogs.push({
        timestamp: new Date().toISOString(),
        message: `Failed to refresh JWT token: ${error.message}`,
        type: 'jwt_refresh_error',
        error: error.response?.data
      });
      localStorage.setItem('debugLogs', JSON.stringify(debugLogs));
      
      return false;
    }
  }, []);

  // Send heartbeat to backend
  const sendHeartbeat = useCallback(async () => {
    try {
      await api.post('/api/heartbeat/');
    } catch (error) {
      console.warn('Failed to send heartbeat:', error);
      // Don't throw error - heartbeat failure shouldn't break the app
    }
  }, []);

  // Log approaching inactivity (early warning)
  const logApproachingInactivity = useCallback(async () => {
    const logMessage = 'User approaching inactivity threshold - logging early warning';
    console.log(logMessage);
    
    // Store in localStorage for persistent debugging
    const debugLogs = JSON.parse(localStorage.getItem('debugLogs') || '[]');
    debugLogs.push({
      timestamp: new Date().toISOString(),
      message: logMessage,
      type: 'approaching_inactivity_warning'
    });
    localStorage.setItem('debugLogs', JSON.stringify(debugLogs));
    
    try {
      // Log approaching inactivity to backend
      const response = await api.post('/api/inactive-logout/', {
        action: 'approaching_inactivity',
        time_remaining: '30 seconds'
      });
      console.log('Approaching inactivity logged successfully:', response.data);
      
      // Store success in localStorage
      debugLogs.push({
        timestamp: new Date().toISOString(),
        message: `Approaching inactivity logged successfully: ${JSON.stringify(response.data)}`,
        type: 'approaching_inactivity_success'
      });
      localStorage.setItem('debugLogs', JSON.stringify(debugLogs));
      
    } catch (error) {
      console.warn('Failed to log approaching inactivity:', error.message);
      // Don't break the flow if this fails
    }
  }, []);

  // Log inactive logout to backend
  const logInactiveLogout = useCallback(async () => {
    const logMessage = 'logInactiveLogout called - attempting to log inactive logout';
    console.log(logMessage);
    
    // Store in localStorage for persistent debugging
    const debugLogs = JSON.parse(localStorage.getItem('debugLogs') || '[]');
    debugLogs.push({
      timestamp: new Date().toISOString(),
      message: logMessage,
      type: 'inactive_logout_attempt'
    });
    localStorage.setItem('debugLogs', JSON.stringify(debugLogs));
    
    try {
      // First try to refresh the token if we have a refresh token
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          console.log('Attempting to refresh token before inactive logout...');
          const refreshResponse = await api.post('/api/token/refresh/', {
            refresh: refreshToken
          });
          
          // Update the access token
          const newAccessToken = refreshResponse.data.access;
          localStorage.setItem('accessToken', newAccessToken);
          
          // Update axios default header
          api.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
          
          console.log('Token refreshed successfully, now calling inactive logout...');
        } catch (refreshError) {
          console.warn('Failed to refresh token:', refreshError.message);
          // Continue anyway - might still work with current token
        }
      }
      
      // Try to call inactive logout endpoint
      const response = await api.post('/api/inactive-logout/');
      const successMessage = `Inactive logout logged successfully: ${JSON.stringify(response.data)}`;
      console.log(successMessage);
      
      // Store success in localStorage
      debugLogs.push({
        timestamp: new Date().toISOString(),
        message: successMessage,
        type: 'inactive_logout_success'
      });
      localStorage.setItem('debugLogs', JSON.stringify(debugLogs));
      
    } catch (error) {
      const errorMessage = `Failed to log inactive logout: ${error.message}`;
      console.error(errorMessage);
      console.error('Error response:', error.response?.data);
      
      // Store error in localStorage
      debugLogs.push({
        timestamp: new Date().toISOString(),
        message: errorMessage,
        type: 'inactive_logout_error',
        error: error.response?.data
      });
      localStorage.setItem('debugLogs', JSON.stringify(debugLogs));
      
      // If it's a 401 error, the token might be expired
      if (error.response?.status === 401) {
        console.warn('Token expired or invalid - cannot log inactive logout to backend');
        // Still proceed with logout - don't break the flow
      }
      
      // Don't break logout if logging fails
    }
  }, []);

  // Track user activity
  const trackActivity = useCallback(() => {
    if (isAuthenticated) {
      const oldTime = lastActivityRef.current;
      lastActivityRef.current = Date.now();
      
      // Debug logging
      console.log(`Activity detected! Timer reset. Old: ${new Date(oldTime).toLocaleTimeString()}, New: ${new Date(lastActivityRef.current).toLocaleTimeString()}`);
      
      // Check if JWT token needs refresh due to user activity
      const tokenExpiry = getTokenExpiry();
      if (tokenExpiry) {
        const timeUntilExpiry = tokenExpiry - Date.now();
        
        // Refresh JWT token if less than 5 minutes remaining
        if (timeUntilExpiry < JWT_REFRESH_THRESHOLD) {
          console.log(`JWT token expires in ${Math.round(timeUntilExpiry / 60000)} minutes - refreshing due to user activity`);
          refreshJWTToken();
        }
      }
    }
  }, [isAuthenticated, getTokenExpiry, refreshJWTToken]);

  // Check if user should be logged out due to inactivity
  const checkInactivity = useCallback(() => {
    if (isAuthenticated) {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityRef.current;
      
      // Debug logging
      console.log(`Inactivity check: ${timeSinceLastActivity}ms since last activity, timeout: ${ACTIVITY_TIMEOUT}ms`);
      
      // Check if approaching inactivity threshold (55 minutes)
      if (timeSinceLastActivity >= WARNING_THRESHOLD && timeSinceLastActivity < ACTIVITY_TIMEOUT) {
        // User is approaching inactivity - log early warning
        console.log('User approaching inactivity threshold (55 minutes) - logging early warning...');
        logApproachingInactivity();
      }
      
      // Check if reached inactivity threshold (1 hour)
      if (timeSinceLastActivity >= ACTIVITY_TIMEOUT) {
        // User has been inactive for 1 hour, logout
        console.log('User inactive for 1 hour, logging out...');
        
        // Log inactive logout to backend BEFORE clearing session
        // This ensures we use the still-valid token
        logInactiveLogout().then(() => {
          // Only trigger logout after logging is complete (or failed)
          console.log('Inactive logout logging completed, now triggering frontend logout...');
          handleSessionExpiration();
        }).catch((error) => {
          // If logging fails, still proceed with logout
          console.warn('Inactive logout logging failed, proceeding with logout anyway:', error);
          handleSessionExpiration();
        });
      }
    }
  }, [isAuthenticated, logApproachingInactivity, logInactiveLogout, handleSessionExpiration]);

  // Set up activity tracking
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // Track meaningful user actions
    const trackableEvents = [
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'submit',
      'change',
      'input'
    ];

    // Add event listeners for user activity
    const handleActivity = () => trackActivity();
    
    trackableEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Set up heartbeat interval
    heartbeatIntervalRef.current = setInterval(() => {
      if (isAuthenticated) {
        sendHeartbeat();
      }
    }, HEARTBEAT_INTERVAL);

    // Set up inactivity check interval (check every 5 minutes for 1-hour timeout)
    const inactivityCheckInterval = setInterval(checkInactivity, 300000);
    console.log('Inactivity check interval set up - checking every 5 minutes');

    // Cleanup function
    return () => {
      trackableEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      
      clearInterval(inactivityCheckInterval);
    };
  }, [isAuthenticated, trackActivity, sendHeartbeat, checkInactivity]);

  // Return function to manually track activity (for programmatic use)
  return { 
    trackActivity,
    // Debug function to check stored logs
    getDebugLogs: () => {
      const logs = JSON.parse(localStorage.getItem('debugLogs') || '[]');
      console.log('=== DEBUG LOGS ===');
      logs.forEach(log => {
        console.log(`${log.timestamp}: ${log.message}`);
        if (log.error) console.log('Error details:', log.error);
      });
      console.log('=== END DEBUG LOGS ===');
      return logs;
    },
    // Clear debug logs
    clearDebugLogs: () => {
      localStorage.removeItem('debugLogs');
      console.log('Debug logs cleared');
    }
  };
};

export default useActivityTracker;
